import {
  Component,
  inject,
  OnInit,
  ViewChild,
  AfterViewInit,
  input,
} from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';

import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogModule } from '@angular/material/dialog';

import { MatDivider } from '@angular/material/divider';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import type { Contract } from '#backend/entity/loaded/crm/Contract';
import { isAbortOrAbortCause } from '../../../shared/utils/is-abort-or-abort-cause';
import debounce from 'lodash/debounce';
import { Temporal } from '@js-temporal/polyfill';
import { TypeSafeMatCellDef } from '../../../type-safe-mat-cell-def.directive';
import { getAppNow, toZonedDateTime } from '../../../shared/utils/date-utils';
import { CnpjPipe } from '../../../shared/cnpj.pipe';

@Component({
  selector: 'app-contract-list',
  standalone: true,
  imports: [
    CnpjPipe,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule,
    MatMenuModule,
    MatDialogModule,
    MatDivider,
    TypeSafeMatCellDef,
  ],
  templateUrl: './contract-list.component.html',
  styleUrls: ['./contract-list.component.scss'],
})
export class ContractListComponent implements OnInit, AfterViewInit {
  private orpc = inject(SmartOrpcClientService).client;
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  readonly organizationId = input<number>();
  readonly showSubtitle = input<boolean>(true);
  private abortController?: AbortController;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = [
    'contractNumber',
    'organization',
    'period',
    'modality',
    'quantities',
    'status',
    'actions',
  ];

  dataSource = new MatTableDataSource<Contract>([]);
  totalContracts = 0;
  pageSize = 10;
  pageIndex = 0;
  isLoading = false;
  searchText = '';
  sortActive = 'createdDate';
  sortDirection: 'asc' | 'desc' = 'desc';

  ngOnInit(): void {
    this.loadContracts();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  async loadContracts(searchText?: string): Promise<void> {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.isLoading = true;
    this.searchText = searchText || '';

    try {
      const [contracts, total] = await this.orpc.contract.list(
        {
          organizationId: this.organizationId() ?? undefined,
          textSearch: this.searchText,
          limit: this.pageSize,
          cursor: this.pageIndex * this.pageSize,
        },
        { signal: this.abortController.signal },
      );

      this.dataSource.data = contracts;
      this.totalContracts = total;
    } catch (error) {
      if (isAbortOrAbortCause(error)) return;
      console.error('Error loading contracts:', error);
      this.snackBar.open('Erro ao carregar contratos', 'Fechar', {
        duration: 3000,
      });
    } finally {
      this.isLoading = false;
    }
  }

  onSearch = debounce((event: Event) => {
    const searchValue = (event.target as HTMLInputElement).value;
    this.pageIndex = 0;
    this.loadContracts(searchValue);
  }, 500);

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadContracts(this.searchText);
  }

  onSortChange(event: Sort): void {
    this.sortActive = event.active;
    this.sortDirection = event.direction as 'asc' | 'desc';
    this.pageIndex = 0;
    this.loadContracts(this.searchText);
  }

  onCreate(): void {
    this.router.navigate(['/admin/contracts/new'], {
      queryParams: {
        organizationId: this.organizationId(),
      },
    });
  }

  onEdit(contract: Contract): void {
    this.router.navigate(['/admin/contracts/edit', contract.id]);
  }

  onView(contract: Contract): void {
    this.router.navigate(['/admin/contracts/view', contract.id]);
  }

  async onDelete(contract: Contract): Promise<void> {
    const confirmed = confirm(
      `Deseja realmente excluir o contrato ${contract.contractNumber}?\n\nEsta ação não pode ser desfeita.`,
    );

    if (confirmed) {
      try {
        await this.orpc.contract.delete(contract.id!);
        this.snackBar.open('Contrato excluído com sucesso!', 'Fechar', {
          duration: 3000,
        });
        this.loadContracts(this.searchText);
      } catch (error) {
        console.error('Error deleting contract:', error);
        this.snackBar.open('Erro ao excluir contrato', 'Fechar', {
          duration: 3000,
        });
      }
    }
  }

  getModalityLabel(modality: string): string {
    return modality === 'with_commitment'
      ? 'Com compromisso'
      : 'Sem compromisso';
  }

  getModalityChipClass(modality: string): string {
    return modality === 'with_commitment'
      ? 'modality-with-commitment'
      : 'modality-without-commitment';
  }

  formatDateRange(startDate: string, endDate: string): string {
    const start = Temporal.PlainDate.from(startDate).toLocaleString();
    const end = Temporal.PlainDate.from(endDate).toLocaleString();
    return `${start} - ${end}`;
  }

  getContractStatus(contract: Contract): string {
    const now = getAppNow().epochMilliseconds;
    const startDate = toZonedDateTime(contract.startDate).epochMilliseconds;
    const endDate = toZonedDateTime(contract.endDate).epochMilliseconds;

    if (now < startDate) {
      return 'Não iniciado';
    } else if (now > endDate) {
      return 'Expirado';
    } else {
      return 'Ativo';
    }
  }

  getStatusChipClass(contract: Contract): string {
    const status = this.getContractStatus(contract);
    switch (status) {
      case 'Ativo':
        return 'status-active';
      case 'Não iniciado':
        return 'status-pending';
      case 'Expirado':
        return 'status-expired';
      default:
        return 'status-unknown';
    }
  }

  getQuantitiesSummary(contract: Contract): string {
    const isCommit = contract.modality === 'with_commitment';
    const c = contract.complianceReportsLimit;
    const h = contract.historyReportsLimit;
    const m = contract.monitoringHectaresLimit;

    if (isCommit) {
      const compliance = c ?? 0;
      const history = h ?? 0;
      const monitoring = m ?? 0;

      return `C:\u00a0${compliance} | H:\u00a0${history} | M:\u00a0${monitoring}\u00a0ha`;
    } else {
      const compliance = c === null ? '∞' : 0;
      const history = h === null ? '∞' : 0;
      const monitoring = m === null ? '∞' : 0;

      return `C:\u00a0${compliance} | H:\u00a0${history} | M:\u00a0${monitoring}${typeof monitoring === 'number' ? '\u00a0ha' : ''}`;
    }
  }

  refreshData(): void {
    this.pageIndex = 0;
    this.loadContracts(this.searchText);
  }

  getPeriodDurationInDays(startDate: string, endDate: string): number {
    const start = Temporal.PlainDate.from(startDate);
    const end = Temporal.PlainDate.from(endDate);
    return start.until(end).days;
  }
}
